import React, { memo, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons'
import { Button, Card, Form, Input, message, Rate, Space } from 'antd'

import { ignoreNPS, postNPS } from 'app-redux/nps/actions'
import { Send, Select } from 'components/UI'
import { compareWithJSON } from 'utils/helper'
import { useEditNotificationOptionMutation } from 'utils/queries'
import styles from './style.module.scss'

const Character = ({ index, value }) => {
  return (
    <div className={`${styles.char} ${value >= index + 1 ? styles.focusedChar : ''}`}>{index}</div>
  )
}

const NPS = ({ muteDuration: propMuteDuration, showNps }) => {
  const { formatMessage } = useIntl()
  const [rate, setRate] = useState()
  const [loading, setLoading] = useState(false)
  const [loadingClose, setLoadingClose] = useState(false)
  const [sent, setSent] = useState(false)
  const [muteDuration, setMuteDuration] = useState(() => propMuteDuration || 1)

  const dispatch = useDispatch()

  const editNotificationMutation = useEditNotificationOptionMutation()

  const muteOptions = useMemo(() => {
    const monthLabel = formatMessage({ id: 'date.month' })
    const monthsLabel = formatMessage({ id: 'date.months' })
    return Array.from({ length: 12 }, (_, i) => ({
      label: `${i + 1} ${i === 0 ? monthLabel : monthsLabel}`.toLowerCase(),
      value: i + 1,
    }))
  }, [formatMessage])

  const closeNPS = async () => {
    if (sent) {
      setSent(false)
      return
    }
    if (loadingClose) {
      return
    }
    try {
      setLoadingClose(true)
      const response = await dispatch(ignoreNPS())
      editNotificationMutation.mutate(
        {
          name: 'notification_show_nps_mute',
          value: muteDuration,
        },
        {
          onError: (error) => {
            if (error.data.message) {
              message.error(error.data.message)
            }
          },
        },
      )
      setLoadingClose(false)
      if (response.data.success) {
        message.success(response.data.message)
      } else {
        message.error(response.data.message)
      }
    } catch (e) {
      console.log(e)
      setLoadingClose(false)
      message.error(formatMessage({ id: 'nps.cannot_hide_nps' }))
    }
  }

  const sendNPS = async (values) => {
    try {
      setLoading(true)
      const response = await dispatch(
        postNPS({
          ...values,
          score: rate - 1,
        }),
      )
      editNotificationMutation.mutate(
        {
          name: 'notification_show_nps_mute',
          value: muteDuration,
        },
        {
          onError: (error) => {
            if (error.data.message) {
              message.error(error.data.message)
            }
          },
        },
      )
      setLoading(false)
      if (response.data.success) {
        setSent(true)
        message.success(response.data.message)
      } else {
        message.error(response.data.message)
      }
    } catch (e) {
      console.log(e)
      setLoading(false)
      message.error(formatMessage({ id: 'nps.cannot_send_nps' }))
    }
  }

  if (!showNps && !sent) {
    return null
  }

  return (
    <Card
      className={styles.npsContainer}
      title={formatMessage({
        id: sent ? 'nps.thank_you_your_input' : 'nps.how_likely_you_recommend',
      })}
      styles={{
        body: sent ? { padding: 0 } : {},
        header: {
          backgroundColor: 'rgb(1, 144, 254)',
          color: 'white',
          fontWeight: 'bold',
          padding: 10,
        },
      }}
    >
      <Button
        type="primary"
        shape="circle"
        className={styles.closeBtn}
        icon={loadingClose ? <LoadingOutlined /> : <CloseOutlined />}
        onClick={closeNPS}
      />
      {!sent && (
        <>
          <Rate count={11} character={Character} onChange={(val) => setRate(val)} value={rate} />
          <div className={styles.tooltipWrapper}>
            <span>
              <b>0</b>
              {` - ${formatMessage({ id: 'nps.not_likely' })}`}
            </span>
            <span>
              <b>10</b>
              {` - ${formatMessage({ id: 'nps.very_likely' })}`}
            </span>
          </div>
          <Form
            layout="vertical"
            onFinish={sendNPS}
            className={`${styles.formWrapper} ${rate ? styles.showForm : ''}`}
          >
            <Form.Item name="message" label={formatMessage({ id: 'nps.please_mention_another' })}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 3 }} />
            </Form.Item>
            <Form.Item>
              <Send htmlType="submit" style={{ float: 'right' }} loading={loading} />
            </Form.Item>
          </Form>
          <Space style={{ marginTop: '1rem', width: '100%', justifyContent: 'space-between' }}>
            <span>{formatMessage({ id: 'notification.show_nps_mute' })}</span>
            <Select
              value={muteDuration}
              onChange={setMuteDuration}
              options={muteOptions}
              style={{ width: 150 }}
            />
          </Space>
        </>
      )}
      {sent && rate >= 10 && (
        <div className={styles.googleReviewWrapper}>
          <p>{formatMessage({ id: 'nps.happy_to_hear' })}</p>
          <Button
            type="primary"
            size="large"
            block
            onClick={() => {
              window.open(
                'https://www.google.com/search?sca_esv=bc0aa39c15f4c826&sxsrf=AE3TifPcsWOvCbmeUXzfyv4jCfsAmJ5XeA:1761873731342&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-Eyoh32KSDms9pxRwdLSYTDc4p6RBVnLH80SIDOHokSoq2p15835SJ0IGOdwfHEbse9jVswLkIOJ4aKP2Ej2Cr6p7yAOY&q=Kledo+Ulasan&sa=X&ved=2ahUKEwjthZWPo82QAxV9XGwGHQl1CvIQ0bkNegQIIhAE&biw=1680&bih=840&dpr=2',
                '_blank',
                'noopener,noreferrer',
              )
            }}
          >
            {formatMessage({ id: 'nps.review_on_google' })}
          </Button>
        </div>
      )}
    </Card>
  )
}

export default memo(NPS, compareWithJSON)
